import useAppLanguage from 'hooks/useAppLanguage';
import { useEffect, useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';
import PromoBanner from 'app/_authenticated/portfolio/components/PromoBanner/PromoBanner';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import Dropdown from 'components/Dropdown/Dropdown';
import PerformanceGraph from 'components/PerformanceGraph/PerformanceGraph';
import { SEO } from 'components/Seo/Seo';
import { IconTabItem, IconTabs } from 'components/Tabs/IconTabs';
import appUrls from 'utils/app-urls';
import { dropdownTimeframeOptions } from 'utils/constants';
import PortfolioSummary from './components/PortfolioSummary/PortfolioSummary';

import { Typography } from '@altify/components';
import { useContent } from '@altify/utils';
import GainLoss from 'components/GainLoss/GainLoss';
import { InfoButton } from 'components/InfoButton/InfoButton';
import { TimeFrameKey } from 'data/time-frames';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import { useUpdateAnalyticsData } from 'hooks/useUpdateAnalyticsData';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import PortfolioInvestmentJourney from './components/PortfolioInvestmentJourney/PortfolioInvestmentJourney';
import { gainLossContent } from './gainLoss.content';
import messages from './messages';
import PortfolioHoldingsTable from './components/PortfolioHoldingsTable';
import useBreakpoint from 'hooks/useBreakpoint';
import { getInvestmentJourneyStatus } from 'utils/investmentJourney';
import AiInsightsTabs from 'components/Tabs/AiInsightsTabs';
import { useAiInsights } from '../../../components/AiInsights/aiInsights-context';
import clsx from 'clsx';

const cdnUrl = appUrls.getCDNUrl();

export const Route = createFileRoute('/_authenticated/portfolio')({
  component: Portfolio,
});

function Portfolio() {
  const { isSmaller } = useBreakpoint();
  const isDeviceMobile = isSmaller('lg');

  const language = useAppLanguage();
  const { data: profileData, isLoading: isLoadingProfile } =
    useGetProfileQuery();
  const { functionalCurrency: baseCurrency } = useFunctionalCurrency();
  const [timeIntervalKey, setTimeIntervalKey] = useState<TimeFrameKey>('24h');
  const { products, isLoading: isLoadingProducts } =
    useProductsWithHoldingsAndRates();
  const { gainsAndLosses, isLoading: isLoadingPortfolioGains } =
    usePortfolioGainsAndLosses(timeIntervalKey);
  useUpdateAnalyticsData();

  const localizedGainLossInfoContent = useContent(gainLossContent);

  const investmentJourneyStatus = profileData
    ? getInvestmentJourneyStatus(profileData)
    : null;

  const isLoading =
    isLoadingProducts || isLoadingProfile || isLoadingPortfolioGains;

  const hasProductsWithHoldings =
    products && products.filter(p => p?.totalAmount > 0).length > 0;

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const iconTabs: IconTabItem[] = [
    {
      id: 'FEATURE_CONTENT',
      icon: `${cdnUrl}assets/portfolio/lightning.svg`,
      action: () => setActiveTabIndex(0),
    },
    {
      id: 'PERFORMANCE_GRAPH',
      icon: `${cdnUrl}assets/portfolio/linechart.svg`,
      action: () => setActiveTabIndex(1),
    },
  ];

  const portfolioMessages = messages[language];
  const { openAiInsights } = useAiInsights();
  return (
    <>
      <SEO
        title={portfolioMessages.seo.title}
        description={portfolioMessages.seo.description}
        keywords={portfolioMessages.seo.keywords}
        page="portfolio"
      />

      {/* {isLoading && <CustomLoader page />} */}
      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row w-full">
          <div className="flex flex-1 flex-grow w-full lg:w-1/2">
            <PortfolioSummary
              timeFrame={timeIntervalKey}
              showGains
              onChangeTimeFrame={setTimeIntervalKey}
            />
          </div>
          <div className="hidden lg:flex lg:w-1/2 w-full flex-col flex-1 md:ml-2 lg:ml-0">
            {isLoading ? (
              <div className="hidden lg:flex bg-background m-4 ml-0 rounded-3xl  flex-col flex-1">
                <CustomLoader />
              </div>
            ) : (
              <>
                {investmentJourneyStatus === 'INVESTMENT_JOURNEY_COMPLETE' ? (
                  <>
                    <div className="hidden md:flex flex-col flex-1 m-4 mt-10 xl:mt-4 ml-0">
                      {activeTabIndex === 0 && (
                        <div className="h-full w-full bg-background flex flex-col flex-1 rounded-3xl  overflow-hidden">
                          <PromoBanner
                            headerControls={
                              <IconTabs
                                items={iconTabs}
                                activeIndex={activeTabIndex}
                              />
                            }
                          />
                        </div>
                      )}

                      {activeTabIndex === 1 && (
                        <PerformanceGraph
                          timeFrame={timeIntervalKey}
                          headerControls={
                            <div className="w-full flex flex-row justify-between">
                              <div>
                                <IconTabs
                                  items={iconTabs}
                                  activeIndex={activeTabIndex}
                                />
                                <div className="mt-2">Portfolio Value</div>
                                {gainsAndLosses && (
                                  <div className="flex flex-row items-center gap-2">
                                    <GainLoss
                                      value={gainsAndLosses.gainAmount ?? 0}
                                      symbol={baseCurrency?.symbol!}
                                    />
                                    <GainLoss
                                      value={gainsAndLosses.gainPercentage ?? 0}
                                      background
                                    />
                                    <Typography>
                                      {
                                        portfolioMessages.overTimePeriod[
                                          timeIntervalKey
                                        ]
                                      }
                                    </Typography>
                                    <InfoButton
                                      title={
                                        localizedGainLossInfoContent.infoTitle
                                      }
                                      text={
                                        localizedGainLossInfoContent.infoText
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                              <Dropdown
                                options={dropdownTimeframeOptions}
                                value={timeIntervalKey}
                                onChange={setTimeIntervalKey}
                              />
                            </div>
                          }
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <PortfolioInvestmentJourney
                    isLoading={isLoading}
                    activeEntry={investmentJourneyStatus}
                  />
                )}
              </>
            )}
          </div>

          {/*  Mobile Accordion */}
          <div className="block lg:hidden w-full">
            {isLoading ? null : (
              <>
                {investmentJourneyStatus !== 'INVESTMENT_JOURNEY_COMPLETE' && (
                  <PortfolioInvestmentJourney
                    isLoading={isLoading}
                    activeEntry={investmentJourneyStatus}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {hasProductsWithHoldings && (
          <div
            className={clsx(
              'flex top-0 z-30 sticky px-2',
              !isDeviceMobile &&
                'whitespace-nowrapoverflow-x-auto no-scrollbar mx-4',
              isDeviceMobile && 'w-full py-4 bg-body-background',
            )}>
            <AiInsightsTabs
              pageName="Portfolio"
              onClickItem={(question: string) => {
                openAiInsights(question, 'Portfolio');
              }}
              className="pl-2 gap-2"
            />
          </div>
        )}
        <PortfolioHoldingsTable
          isMobile={isDeviceMobile}
          messages={portfolioMessages}
        />
      </div>
    </>
  );
}

export default Portfolio;
